import { ChevronDown, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../context/AuthContext';
import Logo from '../ui/Logo';
import RESOURCES from '../../config/resources';
import AuthorSection from './AuthorSection';
import BehavioralAISection from './BehavioralAISection';
import BookSection from './BookSection';
import FeaturesSection from './FeaturesSection';
import HeroSection from './HeroSection';
import PricingSection from './PricingSection';
import ResourcesSection from './ResourcesSection';

const NAV_ITEMS = [
  ['features',      'Features'],
  ['behavioral-ai', 'AI Coach'],
  ['pricing',       'Pricing'],
  ['book',          'The Book'],
  ['resources',     'Resources'],
];

function LandingNav({ isAuthenticated }) {
  const [visible, setVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        visible ? 'bg-[#0A1628]/95 backdrop-blur-md shadow-lg shadow-black/40 py-3' : 'py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Logo wordmark size={30} />

        <div className="hidden md:flex items-center gap-7">
          {NAV_ITEMS.map(([id, label]) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="text-slate-300 hover:text-emerald-400 text-sm font-medium transition-colors"
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <Link
              to="/"
              className="bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              Dashboard →
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="text-slate-300 hover:text-white text-sm font-medium transition-colors hidden sm:block"
              >
                Sign In
              </Link>
              <Link
                to="/login?mode=register"
                className="bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              >
                Start Free
              </Link>
            </>
          )}
          <button
            className="md:hidden text-slate-300 hover:text-white ml-1"
            onClick={() => setMenuOpen(m => !m)}
          >
            <ChevronDown size={20} className={`transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-[#0A1628]/98 border-t border-slate-700/50 px-6 py-4 flex flex-col gap-4">
          {NAV_ITEMS.map(([id, label]) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="text-slate-300 hover:text-emerald-400 text-sm font-medium text-left transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

function ScrollTopButton() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-8 right-8 z-50 w-11 h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg flex items-center justify-center transition-all"
    >
      <ChevronDown size={20} className="rotate-180" />
    </button>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const isAuthenticated = !!user;

  return (
    <div className="min-h-screen bg-[#0A1628] text-white font-sans">
      <Helmet>
        <title>Trading Journal — Track, Analyze & Improve Your Trades</title>
        <meta name="description" content="The trading journal with behavioral AI. Sync with Alpaca & Schwab, AI edge analysis, behavioral pattern detection, and playbooks. Free to start." />
        <meta property="og:title" content="Trading Journal — Track, Analyze & Improve Your Trades" />
        <meta property="og:description" content="Know your edge. Fix your behavior. Trade better. AI-powered analytics, broker sync, and journaling — free to start." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Trading Journal — Know Your Edge. Fix Your Behavior." />
        <meta name="twitter:description" content="Free trading journal with broker sync, behavioral AI analysis, and trade journaling. Start free." />
      </Helmet>

      <LandingNav isAuthenticated={isAuthenticated} />

      <section id="hero">
        <HeroSection isAuthenticated={isAuthenticated} />
      </section>

      <section id="features">
        <FeaturesSection isAuthenticated={isAuthenticated} />
      </section>

      <BehavioralAISection isAuthenticated={isAuthenticated} />

      <PricingSection isAuthenticated={isAuthenticated} />

      <BookSection />

      <section id="resources">
        <ResourcesSection resources={RESOURCES} />
      </section>

      <section id="author">
        <AuthorSection />
      </section>

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-emerald-500/10 via-slate-800/20 to-emerald-500/10 border-y border-emerald-500/15 py-16 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <TrendingUp size={40} className="text-emerald-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-3">Know your edge. Fix your behavior.</h2>
          <p className="text-slate-400 mb-8">
            Start free. Your trade data has patterns you haven't seen yet — the AI Coach surfaces them for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isAuthenticated ? (
              <Link to="/" className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-8 py-3 rounded-xl transition-colors text-lg">
                Open Dashboard
              </Link>
            ) : (
              <Link to="/login?mode=register" className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold px-8 py-3 rounded-xl transition-colors text-lg">
                Create Free Account
              </Link>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-[#060e1a] border-t border-slate-800 py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Logo size={28} />
            <span className="text-slate-400 text-sm">© 2026 Sushil Sharma — CNC Publishing. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-300 transition-colors">Terms of Service</Link>
            <Link to="/disclaimer" className="hover:text-slate-300 transition-colors">Disclaimer</Link>
            <a href="mailto:tech.sushil@gmail.com" className="hover:text-slate-300 transition-colors">Contact</a>
          </div>
        </div>
        <p className="text-center text-slate-700 text-xs mt-6 max-w-3xl mx-auto">
          The resources on this site are for educational purposes only and do not constitute financial advice.
        </p>
      </footer>

      <ScrollTopButton />
    </div>
  );
}
